import { EErrorStatus, IError } from "@axmit/error-helper";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import { ERoutesPrivate } from "common/models/routesModel";
import { EBitecoinPageTabKeys } from "app/pages/Franchisor/BitecoinPage";
import { AppDispatch, history } from 'app/store';
import { IBitecoinRewardCategoryCollection, IBitecoinRewardCategoryCollectionFilter, IBitecoinRewardCategoryCreateParams, IBitecoinRewardCategoryModel, IBitecoinRewardCollection, IBitecoinRewardCollectionFilter, IBitecoinRewardCreateParams, IBitecoinRewardModel, IBitecoinSettingsModel, IBitecoinSettingsParams } from "entities/Bitecoin/Bitecoin.models";
import { bitecoinTransport } from "entities/Bitecoin/Bitecoin.transport";
import { ECategorySuccessMessage } from "entities/Category/Category.models";
import { EMenuItemSuccessMessage } from "entities/MenuItem/MenuItem.models";

export interface IState {
  bitecoinSettingsModelLoading: boolean;
  bitecoinSettingsModel: IBitecoinSettingsModel | null;
  bitecoinSettingsModelError: IError | null;
  bitecoinSettingsModelParams: IBitecoinSettingsParams | null;
  // reward categories
  bitecoinRewardCategoryCollectionLoading: boolean;
  bitecoinRewardCategoryCollection: IBitecoinRewardCategoryCollection | null;

  bitecoinRewardCategoryModelLoading: boolean;
  bitecoinRewardCategoryModel: IBitecoinRewardCategoryModel | null;
  bitecoinRewardCategoryModelError: IError | null;
  bitecoinRewardCategoryModelParams: IBitecoinRewardCategoryCreateParams | null;
  // rewards
  bitecoinRewardCollectionLoading: boolean;
  bitecoinRewardCollection: IBitecoinRewardCollection | null;

  bitecoinRewardModelLoading: boolean;
  bitecoinRewardModel: IBitecoinRewardModel | null;
  bitecoinRewardModelError: IError | null;
  bitecoinRewardModelParams: IBitecoinRewardCreateParams | null;
}

const initialState: IState = {
  bitecoinSettingsModelLoading: false,
  bitecoinSettingsModel: null,
  bitecoinSettingsModelError: null,
  bitecoinSettingsModelParams: null,
  // reward categories
  bitecoinRewardCategoryCollectionLoading: false,
  bitecoinRewardCategoryCollection: null,

  bitecoinRewardCategoryModelLoading: false,
  bitecoinRewardCategoryModel: null,
  bitecoinRewardCategoryModelError: null,
  bitecoinRewardCategoryModelParams: null,
  // rewards
  bitecoinRewardCollectionLoading: false,
  bitecoinRewardCollection: null,

  bitecoinRewardModelLoading: false,
  bitecoinRewardModel: null,
  bitecoinRewardModelError: null,
  bitecoinRewardModelParams: null,
};

export const bitecoinSlice = createSlice({
  name: 'bitecoin',
  initialState,
  reducers: {
    setBitecoinSettingsModel(state, action: PayloadAction<IBitecoinSettingsModel | null>) {
      state.bitecoinSettingsModel = action.payload;
    },
    setBitecoinSettingsModelLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinSettingsModelLoading = action.payload;
    },
    setBitecoinSettingsModelError(state, action: PayloadAction<IError | null>) {
      state.bitecoinSettingsModelError = action.payload;
    },
    setBitecoinSettingsModelParams(state, action: PayloadAction<IBitecoinSettingsParams | null>) {
      state.bitecoinSettingsModelParams = action.payload;
    },

    // reward category collection
    setBitecoinRewardCategoryCollectionLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinRewardCategoryCollectionLoading = action.payload;
    },
    setBitecoinRewardCategoryCollection(state, action: PayloadAction<IBitecoinRewardCategoryCollection | null>) {
      state.bitecoinRewardCategoryCollection = action.payload;
    },

    // reward category model
    setBitecoinRewardCategoryModelLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinRewardCategoryModelLoading = action.payload;
    },
    setBitecoinRewardCategoryModel(state, action: PayloadAction<IBitecoinRewardCategoryModel | null>) {
      state.bitecoinRewardCategoryModel = action.payload;
    },
    setBitecoinRewardCategoryModelError(state, action: PayloadAction<IError | null>) {
      state.bitecoinRewardCategoryModelError = action.payload;
    },
    setBitecoinRewardCategoryModelParams(state, action: PayloadAction<IBitecoinRewardCategoryCreateParams | null>) {
      state.bitecoinRewardCategoryModelParams = action.payload;
    },

    // reward collection
    setBitecoinRewardCollectionLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinRewardCollectionLoading = action.payload;
    },
    setBitecoinRewardCollection(state, action: PayloadAction<IBitecoinRewardCollection | null>) {
      state.bitecoinRewardCollection = action.payload;
    },

    // reward model
    setBitecoinRewardModelLoading(state, action: PayloadAction<boolean>) {
      state.bitecoinRewardModelLoading = action.payload;
    },
    setBitecoinRewardModel(state, action: PayloadAction<IBitecoinRewardModel | null>) {
      state.bitecoinRewardModel = action.payload;
    },
    setBitecoinRewardModelError(state, action: PayloadAction<IError | null>) {
      state.bitecoinRewardModelError = action.payload;
    },
    setBitecoinRewardModelParams(state, action: PayloadAction<IBitecoinRewardCreateParams | null>) {
      state.bitecoinRewardModelParams = action.payload;
    },
  }
});

export const {
  setBitecoinSettingsModel,
  setBitecoinSettingsModelLoading,
  setBitecoinSettingsModelError,
  setBitecoinSettingsModelParams,
  // reward categories
  setBitecoinRewardCategoryCollectionLoading,
  setBitecoinRewardCategoryCollection,
  setBitecoinRewardCategoryModelLoading,
  setBitecoinRewardCategoryModel,
  setBitecoinRewardCategoryModelError,
  setBitecoinRewardCategoryModelParams,
  // rewards
  setBitecoinRewardCollectionLoading,
  setBitecoinRewardCollection,
  setBitecoinRewardModelLoading,
  setBitecoinRewardModel,
  setBitecoinRewardModelError,
  setBitecoinRewardModelParams,
} = bitecoinSlice.actions;
export default bitecoinSlice.reducer;

export const getBitecoinSettings = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinSettingsModelLoading(true));

    try {
      const bitecoinSettingsModel = await bitecoinTransport.getBitecoinSettings();

      dispatch(setBitecoinSettingsModel(bitecoinSettingsModel));

    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinSettingsModelError(_error));
    } finally {
      dispatch(setBitecoinSettingsModelLoading(false));
    }
  }

  return thunk;
};

export const updateBitecoinSettings = (params: IBitecoinSettingsParams, successCb: () => void) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinSettingsModelLoading(true));
    dispatch(setBitecoinSettingsModelParams(params));

    try {
      const bitecoinSettingsModel = await bitecoinTransport.updateBitecoinSettings(params);

      dispatch(setBitecoinSettingsModel(bitecoinSettingsModel));
      dispatch(setBitecoinSettingsModelParams(null));

      successCb();
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinSettingsModelError(_error));
    } finally {
      dispatch(setBitecoinSettingsModelLoading(false));
    }
  }

  return thunk;
};

// reward categories
export const getBitecoinRewardCategoryCollection = (filter: IBitecoinRewardCategoryCollectionFilter) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCategoryCollectionLoading(true));

    try {
      const bitecoinRewardCategoryCollection = await bitecoinTransport.getBitecoinRewardCategoryCollection(filter);

      dispatch(setBitecoinRewardCategoryCollection(bitecoinRewardCategoryCollection));
    } catch (error) {
      console.log('getBitecoinRewardCategoryCollection error', error);
    } finally {
      dispatch(setBitecoinRewardCategoryCollectionLoading(false));
    }
  }

  return thunk;
};

export const getBitecoinRewardCategoryById = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCategoryModelLoading(true));

    try {
      const bitecoinRewardCategoryModel = await bitecoinTransport.getBitecoinRewardCategory(id);

      dispatch(setBitecoinRewardCategoryModel(bitecoinRewardCategoryModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinRewardCategoryModelError(_error));
    } finally {
      dispatch(setBitecoinRewardCategoryModelLoading(false));
    }
  }

  return thunk;
};

export const addBitecoinRewardCategory = (params: IBitecoinRewardCategoryCreateParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCategoryModelLoading(true));
    dispatch(setBitecoinRewardCategoryModelParams(params));

    try {
      const bitecoinRewardCategoryModel = await bitecoinTransport.addBitecoinRewardCategory(params);

      dispatch(setBitecoinRewardCategoryModel(bitecoinRewardCategoryModel));
      dispatch(setBitecoinRewardCategoryModelParams(null));
      dispatch(setBitecoinRewardCategoryModelError(null));

      message.success(ECategorySuccessMessage.Create);
      history.push(`${ERoutesPrivate.Bitecoin}?tab=${EBitecoinPageTabKeys.RewardCategories}`);
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinRewardCategoryModelError(_error));
    } finally {
      dispatch(setBitecoinRewardCategoryModelLoading(false));
    }
  }

  return thunk;
};

export const updateBitecoinRewardCategory = (id: string, params: IBitecoinRewardCategoryCreateParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCategoryModelLoading(true));
    dispatch(setBitecoinRewardCategoryModelParams(params));

    try {
      const bitecoinRewardCategoryModel = await bitecoinTransport.updateBitecoinRewardCategory({ id, params });

      dispatch(setBitecoinRewardCategoryModel(bitecoinRewardCategoryModel));
      dispatch(setBitecoinRewardCategoryModelParams(null));
      dispatch(setBitecoinRewardCategoryModelError(null));

      message.success(ECategorySuccessMessage.Edit);
      history.push(`${ERoutesPrivate.Bitecoin}?tab=${EBitecoinPageTabKeys.RewardCategories}`);
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinRewardCategoryModelError(_error));
    } finally {
      dispatch(setBitecoinRewardCategoryModelLoading(false));
    }
  }

  return thunk;
};

export const deleteBitecoinRewardCategory = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCategoryModelLoading(true));

    try {
      await bitecoinTransport.deleteBitecoinRewardCategory(id);

      message.success(ECategorySuccessMessage.Delete);
      history.push(`${ERoutesPrivate.Bitecoin}?tab=${EBitecoinPageTabKeys.RewardCategories}`);
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinRewardCategoryModelError(_error));
    } finally {
      dispatch(setBitecoinRewardCategoryModelLoading(false));
    }
  }

  return thunk;
};

// rewards
export const getBitecoinRewardCollection = (filter: IBitecoinRewardCollectionFilter) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardCollectionLoading(true));

    try {
      const bitecoinRewardCollection = await bitecoinTransport.getBitecoinRewardCollection(filter);

      dispatch(setBitecoinRewardCollection(bitecoinRewardCollection));
    } catch (error) {
      console.log('getBitecoinRewardCollection error', error);
    } finally {
      dispatch(setBitecoinRewardCollectionLoading(false));
    }
  }

  return thunk;
};

export const getBitecoinRewardById = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardModelLoading(true));

    try {
      const bitecoinRewardModel = await bitecoinTransport.getBitecoinReward(id);

      dispatch(setBitecoinRewardModel(bitecoinRewardModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinRewardModelError(_error));
    } finally {
      dispatch(setBitecoinRewardModelLoading(false));
    }
  }

  return thunk;
};

export const addBitecoinReward = (params: IBitecoinRewardCreateParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardModelLoading(true));
    dispatch(setBitecoinRewardModelParams(params));

    try {
      await bitecoinTransport.addBitecoinReward(params);

      message.success(EMenuItemSuccessMessage.Create);
      history.push(`${ERoutesPrivate.Bitecoin}?tab=${EBitecoinPageTabKeys.RewardItems}`);

      dispatch(setBitecoinRewardModel(null));
      dispatch(setBitecoinRewardModelParams(null));
      dispatch(setBitecoinRewardModelError(null));
    } catch (error) {
      const _error = error as IError;

      if (_error.status === EErrorStatus.BadRequest) {
        message.error(_error.data.message);
      }

      dispatch(setBitecoinRewardModelError(_error));
    } finally {
      dispatch(setBitecoinRewardModelLoading(false));
    }
  }

  return thunk;
};

export const updateBitecoinReward = (id: string, params: IBitecoinRewardCreateParams) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardModelLoading(true));
    dispatch(setBitecoinRewardModelParams(params));

    try {
      await bitecoinTransport.updateBitecoinReward({ id, params });

      message.success(EMenuItemSuccessMessage.Edit);
      history.push(`${ERoutesPrivate.Bitecoin}?tab=${EBitecoinPageTabKeys.RewardItems}`);

      dispatch(setBitecoinRewardModel(null));
      dispatch(setBitecoinRewardModelParams(null));
      dispatch(setBitecoinRewardModelError(null));
    } catch (error) {
      const _error = error as IError;

      if (_error.status === EErrorStatus.BadRequest) {
        message.error(_error.data.message);
      }

      dispatch(setBitecoinRewardModelError(_error));
    } finally {
      dispatch(setBitecoinRewardModelLoading(false));
    }
  }

  return thunk;
};

export const deleteBitecoinReward = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setBitecoinRewardModelLoading(true));

    try {
      await bitecoinTransport.deleteBitecoinReward(id);

      message.success(EMenuItemSuccessMessage.Delete);
      history.push(`${ERoutesPrivate.Bitecoin}?tab=${EBitecoinPageTabKeys.RewardItems}`);

      dispatch(setBitecoinRewardModel(null));
      dispatch(setBitecoinRewardModelParams(null));
      dispatch(setBitecoinRewardModelError(null));
    } catch (error) {
      const _error = error as IError;

      dispatch(setBitecoinRewardModelError(_error));
    } finally {
      dispatch(setBitecoinRewardModelLoading(false));
    }
  }

  return thunk;
};
